import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Button, IconButton, Slider } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import CustomModal from "~/common/components/Modal/CustomModal";
import PagedView from "~/common/components/PagedView/PagedView";
import ApiEvidenceView from "./ApiEvidenceView";
import DomSnapshotView from "./DomSnapshotView";
import type { WorkflowArtifact } from "./WorkflowArtifactsSection";
import { formatCapturedAt } from "./WorkflowArtifactsSection";

/** primary-gray-light-200 — inline style value, so the hex is carried here.
 *  Rendered by both ora and the Outlook add-in, so no app token module. */
const RULE_COLOR = "#EAECF0";

// Centered, full-bleed artifact viewer for the workflow detail drawer's
// Evidence section. Wraps <CustomModal> (which provides the dialog surface,
// scrollbar offset, and Esc-to-close) around <PagedView> (which provides
// title + subtitle + prev/next + page-dot chrome). Each artifact becomes one
// page; the body component switches on ``artifact.kind``:
//
//   * screenshot          → <ZoomableImage>     (slider zoom + drag pan +
//                                                wheel-to-zoom-toward-cursor)
//   * api_evidence        → <ApiEvidenceView>   (summary card + FormattedJsonView
//                                                for request / response / error)
//   * dom / dom_dump      → <DomSnapshotView>   (sandboxed iframe + escape-hatch
//                                                "Open in new tab" / Download)
//   * other               → minimal fallback card with a Download link.
//
// Keyboard support: ← / → for prev/next (added here); Esc is handled by
// MUI Modal via CustomModal.
//
// Caller responsibility: pre-filter `artifacts` to entries you actually want
// in the gallery (e.g. screenshots, api_evidence non-phase, and dom dumps —
// trace zips open in the Playwright viewer, phase-marker rows render as
// dividers in the list and never enter the gallery).

export interface ArtifactLightboxProps {
  open: boolean;
  artifacts: WorkflowArtifact[];
  startIndex: number;
  onClose: () => void;
  /** When set, api_evidence rows fetch full JSON via the payload proxy. */
  instanceId?: string | null;
  /** Override payload fetch (plugin uses Product API; ora uses Remix route). */
  fetchArtifactPayload?: (
    instanceId: string,
    filename: string,
  ) => Promise<unknown>;
}

function titleFor(artifact: WorkflowArtifact): string {
  const fallback =
    artifact.kind === "screenshot" ? "Screenshot" : "Artifact";
  return (
    artifact.note?.trim() || artifact.step_name?.trim() || fallback
  );
}

// ---------------------------------------------------------------------------
// ZoomableImage — image with an overlaid zoom slider + pointer-drag pan +
// mouse-wheel zoom toward the cursor (Lightroom / Figma muscle memory).
//
// The slider is the discoverable affordance; the wheel is the muscle-memory
// affordance for users who already know how zoom works in other tools.
// Slider value 0 = fully zoomed out (image fits the container at natural
// size or smaller); slider value 1 = MAX_ZOOM × fit-size. When zoomed in,
// drag the image to pan. State auto-resets on each new image (URL change)
// so users always see a fresh image at fit-size.
// ---------------------------------------------------------------------------

const MAX_ZOOM = 4;
// Tuned so one mouse-wheel notch ≈ 10% zoom step at default trackpad/mouse
// settings (deltaY ≈ 100 on a typical wheel notch; 100 * 0.0015 ≈ 0.15
// scaled, but the [0,1] clamp + repeated notches keep it smooth).
const ZOOM_SENSITIVITY = 0.0015;

function ZoomableImage({ url, alt }: { url: string; alt: string }) {
  // Slider position: 0..1. Scale: 1..MAX_ZOOM linear.
  const [zoom, setZoom] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  // Latest zoom / pan stay accessible to the non-passive wheel handler
  // without retriggering the listener's effect on every state change.
  const zoomRef = useRef(zoom);
  const panRef = useRef(pan);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  // Reset zoom + pan whenever the user navigates to a different image. Keeps
  // the experience predictable: each image opens at fit-size, slider on the
  // left.
  useEffect(() => {
    setZoom(0);
    setPan({ x: 0, y: 0 });
  }, [url]);

  const scale = 1 + zoom * (MAX_ZOOM - 1);
  const canPan = zoom > 0;

  // Update zoom and rescale the pan offset. Two cases:
  //   * Slider use (no focal point) — scale pan proportionally so the
  //     image's center stays put. Same fall-out as before:
  //       - newZoom = 0 → pan snaps to (0,0), image re-centers.
  //       - partway → same image point stays near the viewer's focus.
  //   * Wheel use (focal point) — recompute pan so the image point under
  //     the cursor stays under the cursor as the image scales.
  const setZoomAndRecenter = (
    newZoom: number,
    focal?: { x: number; y: number },
  ) => {
    setPan((prevPan) => {
      if (newZoom === 0) return { x: 0, y: 0 };
      const oldScale = 1 + zoomRef.current * (MAX_ZOOM - 1);
      const newScale = 1 + newZoom * (MAX_ZOOM - 1);
      const ratio = newScale / oldScale;
      if (focal) {
        // Keep the image point under the cursor pinned: new_pan = pan*ratio
        // + focal*(1 - ratio). Focal is measured relative to the canvas
        // center, matching the image's ``transform-origin: center center``.
        return {
          x: prevPan.x * ratio + focal.x * (1 - ratio),
          y: prevPan.y * ratio + focal.y * (1 - ratio),
        };
      }
      return { x: prevPan.x * ratio, y: prevPan.y * ratio };
    });
    setZoom(newZoom);
  };

  // Wheel-to-zoom — attached as a non-passive native listener because
  // React's synthetic onWheel is passive by default, so e.preventDefault()
  // there would be a no-op and the page would scroll behind the modal.
  useEffect(() => {
    const node = canvasRef.current;
    if (!node) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = node.getBoundingClientRect();
      const focalX = e.clientX - rect.left - rect.width / 2;
      const focalY = e.clientY - rect.top - rect.height / 2;
      const delta = -e.deltaY * ZOOM_SENSITIVITY;
      const next = Math.min(1, Math.max(0, zoomRef.current + delta));
      if (next === zoomRef.current) return;
      setZoomAndRecenter(next, { x: focalX, y: focalY });
    };
    node.addEventListener("wheel", handler, { passive: false });
    return () => node.removeEventListener("wheel", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!canPan) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: pan.x,
      baseY: pan.y,
    };
    setIsDragging(true);
  };

  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.baseX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.baseY + (e.clientY - dragRef.current.startY),
    });
  };

  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // Some browsers throw when capture was never set; safe to ignore.
    }
    dragRef.current = null;
    setIsDragging(false);
  };

  const cursorClass = !canPan
    ? "cursor-default"
    : isDragging
      ? "cursor-grabbing"
      : "cursor-grab";

  return (
    <div
      ref={canvasRef}
      className={`relative flex items-start justify-center w-full h-full bg-primary-gray-light-50 rounded overflow-hidden select-none ${cursorClass}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      data-testid="artifact-lightbox-canvas"
    >
      <img
        src={url}
        alt={alt}
        draggable={false}
        className="max-w-full max-h-full object-contain pointer-events-none transition-transform"
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
          transformOrigin: "center center",
          transitionDuration: dragRef.current ? "0ms" : "120ms",
        }}
        data-testid="artifact-lightbox-image"
      />
      {/* Zoom slider overlay — semi-transparent, centered along the bottom.
          Pointer-down is stopped so dragging the slider thumb doesn't start a
          pan. */}
      <div
        className="absolute bottom-3 left-1/2 -translate-x-1/2 w-2/3 max-w-md flex items-center gap-3 bg-base-white/70 backdrop-blur-sm rounded-full px-4 py-1 shadow-md cursor-default"
        onPointerDown={(e) => e.stopPropagation()}
        data-testid="artifact-lightbox-zoom"
      >
        <IconButton
          size="small"
          onClick={() => setZoomAndRecenter(0)}
          aria-label="Zoom out fully"
          className="!text-primary-gray-light-700"
        >
          <ZoomOutIcon fontSize="small" />
        </IconButton>
        <Slider
          value={zoom}
          onChange={(_, v) =>
            setZoomAndRecenter(typeof v === "number" ? v : v[0])
          }
          min={0}
          max={1}
          step={0.01}
          aria-label="Zoom level"
          size="small"
          sx={{
            color: "#FFC600", // sunrise
            "& .MuiSlider-thumb": { width: 14, height: 14 },
          }}
          data-testid="artifact-lightbox-zoom-slider"
        />
        <IconButton
          size="small"
          onClick={() => setZoomAndRecenter(1)}
          aria-label="Zoom in fully"
          className="!text-primary-gray-light-700"
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body switch — picks the right viewer component per artifact kind.
// ---------------------------------------------------------------------------

function FallbackView({ artifact }: { artifact: WorkflowArtifact }) {
  return (
    <div
      className="flex flex-col items-center justify-center w-full h-full gap-3 p-6 text-center"
      data-testid="artifact-lightbox-fallback"
    >
      <p className="text-sm text-primary-gray-light-700">
        This artifact type ({artifact.kind}) can't be previewed inline.
      </p>
      <a
        href={artifact.url}
        target="_blank"
        rel="noopener noreferrer"
        download={artifact.filename ?? ""}
        className="inline-flex items-center gap-1 text-sm text-sunrise hover:underline"
      >
        <DownloadIcon fontSize="small" />
        Download
      </a>
    </div>
  );
}

function renderArtifactBody(
  artifact: WorkflowArtifact,
  instanceId?: string | null,
  fetchArtifactPayload?: (
    instanceId: string,
    filename: string,
  ) => Promise<unknown>,
) {
  const kind = artifact.kind;
  if (kind === "screenshot") {
    return <ZoomableImage url={artifact.url} alt={titleFor(artifact)} />;
  }
  if (kind === "api_evidence") {
    return (
      <ApiEvidenceView
        artifact={artifact}
        instanceId={instanceId}
        fetchArtifactPayload={fetchArtifactPayload}
      />
    );
  }
  if (kind === "dom" || kind === "dom_dump") {
    return <DomSnapshotView artifact={artifact} />;
  }
  return <FallbackView artifact={artifact} />;
}

export default function ArtifactLightbox({
  open,
  artifacts,
  startIndex,
  onClose,
  instanceId,
  fetchArtifactPayload,
}: ArtifactLightboxProps) {
  const [pageIndex, setPageIndex] = useState(startIndex);

  // Re-sync to the caller's startIndex whenever the lightbox is reopened.
  // Without this, opening the second screenshot followed by closing and
  // reopening the first would still show the second.
  useEffect(() => {
    if (open) setPageIndex(startIndex);
  }, [open, startIndex]);

  // Keyboard arrow navigation. Esc is owned by the underlying MUI Modal.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" && pageIndex < artifacts.length - 1) {
        setPageIndex(pageIndex + 1);
      } else if (e.key === "ArrowLeft" && pageIndex > 0) {
        setPageIndex(pageIndex - 1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, pageIndex, artifacts.length]);

  if (artifacts.length === 0) return null;

  const pages = artifacts.map((a) => ({
    title: titleFor(a),
    subtitle: formatCapturedAt(a.captured_at) ?? undefined,
    // Each body component fills the available height — combined with the
    // bottom-pinned footer, the page-dots row never moves regardless of the
    // body's intrinsic dimensions.
    content: renderArtifactBody(a, instanceId, fetchArtifactPayload),
  }));

  return (
    <CustomModal
      id="artifact-lightbox"
      open={open}
      onClose={onClose}
      ariaDescribedBy="artifact-lightbox-content"
      // Override CustomModal's default w-full / xl1440:w-[1424px] sizing so
      // the lightbox is ~75% viewport at every breakpoint. The `!` prefix
      // forces these over the default Tailwind classes.
      modalClass="!w-[75vw] !max-w-none xl1440:!w-[75vw] !p-0"
    >
      <div
        id="artifact-lightbox-content"
        className="flex flex-col h-[75vh]"
        data-testid="artifact-lightbox"
      >
        <PagedView
          pages={pages}
          pageIndex={pageIndex}
          onPageChange={setPageIndex}
          ruleColor={RULE_COLOR}
          titleId="artifact-lightbox-title"
          className="flex flex-col flex-1 min-h-0"
          contentClassName="flex-1 min-h-0"
          keepNavButtonsVisible
          headerEndSlot={
            <IconButton
              onClick={onClose}
              aria-label="Close artifact viewer"
              size="small"
              data-testid="artifact-lightbox-close-x"
            >
              <CloseIcon />
            </IconButton>
          }
          footerEndSlot={
            <Button
              variant="outlined"
              onClick={onClose}
              data-testid="artifact-lightbox-close-button"
              className="!ml-2 !normal-case !text-primary-gray-light-700 !border-primary-gray-light-300 hover:!bg-primary-gray-light-50"
            >
              Close
            </Button>
          }
        />
      </div>
    </CustomModal>
  );
}
